import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ReviewStats, dataSamplesApi } from "../../services/api.dataSamples";

/**
 * How much correction the AI needs, from every review saved in AI Review and
 * Data Samples (app.poop_reviews). Only approved photos with a known AI
 * baseline count: a photo rejected as unusable says nothing about its labels.
 */

const LABEL_TITLES: Record<string, string> = {
  bristol_type: "Bristol type",
  consistency: "Consistency",
  shape: "Shape",
  quantity: "Quantity",
  color: "Color",
  health: "Health",
  blood: "Blood",
  mucus: "Mucus",
  floating: "Floating",
};

const pct = (n: number, of: number) => (of ? `${Math.round((100 * n) / of)}%` : "—");
const typeNum = (s: string) => Number(/[1-7]/.exec(s)?.[0] ?? NaN);

const Stat: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <Box sx={{ minWidth: 120 }}>
    <Typography variant="h5" fontWeight={700}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const ReviewStatsPanel: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    dataSamplesApi
      .reviewStats()
      .then((s) => !cancelled && setStats(s))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!stats) return <CircularProgress size={20} />;

  const current = stats.segments.find((s) => s.segment === "current_app");
  const older = stats.segments.find((s) => s.segment === "older_app");
  if (!current || current.approved === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No reviews of current-app photos logged yet. Every save in AI Review or Data Samples review is counted from now
        on.
      </Typography>
    );
  }

  const exact = stats.bristol.filter((b) => b.ai === b.final).reduce((s, b) => s + b.n, 0);
  const withinOne = stats.bristol
    .filter((b) => Math.abs(typeNum(b.ai) - typeNum(b.final)) <= 1)
    .reduce((s, b) => s + b.n, 0);
  const bristolTotal = stats.bristol.reduce((s, b) => s + b.n, 0);
  const confusions = stats.bristol
    .filter((b) => b.ai !== b.final)
    .sort((a, b) => b.n - a.n)
    .slice(0, 5);

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Current app AI, measured on {current.approved.toLocaleString()} checked photo{current.approved === 1 ? "" : "s"}
        {stats.since ? ` reviewed since ${new Date(stats.since).toLocaleDateString()}` : ""}.
      </Typography>
      <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", rowGap: 2 }}>
        <Stat value={pct(current.approved - current.corrected, current.approved)} label="needed no correction" />
        <Stat value={(current.edits / current.approved).toFixed(2)} label="labels corrected per photo" />
        <Stat value={pct(exact, bristolTotal)} label="Bristol type exactly right" />
        <Stat value={pct(withinOne, bristolTotal)} label="Bristol type within one" />
        <Stat
          value={current.rejected.toLocaleString()}
          label={`photo${current.rejected === 1 ? "" : "s"} marked not good for ML`}
        />
      </Stack>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 1fr)" },
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            How often each label is corrected
          </Typography>
          {Object.keys(LABEL_TITLES).map((label) => {
            const changed = stats.perLabel.find((p) => p.label === label)?.changed ?? 0;
            const share = current.approved ? (100 * changed) / current.approved : 0;
            return (
              <Stack key={label} direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{ width: 110, flexShrink: 0 }}>
                  {LABEL_TITLES[label]}
                </Typography>
                <LinearProgress variant="determinate" value={share} sx={{ flexGrow: 1, height: 8, borderRadius: 4 }} />
                <Typography variant="body2" sx={{ width: 96, textAlign: "right", flexShrink: 0 }}>
                  {Math.round(share)}% ({changed.toLocaleString()})
                </Typography>
              </Stack>
            );
          })}
        </Box>
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Most common Bristol corrections
          </Typography>
          {confusions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>AI said</TableCell>
                  <TableCell>Reviewer set</TableCell>
                  <TableCell align="right">Photos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {confusions.map((c) => (
                  <TableRow key={`${c.ai}-${c.final}`}>
                    <TableCell>{c.ai}</TableCell>
                    <TableCell>{c.final}</TableCell>
                    <TableCell align="right">{c.n.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Box>

      {older && older.approved > 0 && (
        <Typography variant="caption" color="text.secondary">
          For comparison, older-app photos (labels were the user's Bristol pick plus GPT suggestions):{" "}
          {pct(older.approved - older.corrected, older.approved)} needed no correction across{" "}
          {older.approved.toLocaleString()} checked photos.
        </Typography>
      )}
    </Stack>
  );
};

export default ReviewStatsPanel;
